/**
 * World bounds of an entity as it renders, for framing it in the editor: splats from
 * their centres (the 2nd-98th percentile per axis, so stray splats far out don't swamp
 * the frame) or their resource bounds, meshes from their mesh instances. The entity's
 * origin says little: splat files keep their capture coordinates, so a splat sits
 * wherever the scan was, often metres from its origin.
 */
import * as pc from 'playcanvas';

export interface RenderBounds {
    center: [number, number, number];
    /** Radius of the sphere around the bounds (half their diagonal). */
    radius: number;
    /** World-space box corners; min[1] is the content's floor (2nd-percentile splat height). */
    min: [number, number, number];
    max: [number, number, number];
}

/**
 * Box between the `q` and `1 - q` quantiles of xyz triples, per axis, over at most
 * `maxSamples` of them. Null when there are no finite points.
 */
export function quantileBox(centers: ArrayLike<number>, q = 0.02, maxSamples = 32768): pc.BoundingBox | null {
    const n = Math.floor(centers.length / 3);
    if (n === 0) return null;
    const step = Math.max(1, Math.floor(n / maxSamples));
    const size = Math.ceil(n / step);
    const xs = new Float32Array(size), ys = new Float32Array(size), zs = new Float32Array(size);
    let k = 0;
    for (let i = 0; i < n; i += step) {
        const x = centers[i * 3], y = centers[i * 3 + 1], z = centers[i * 3 + 2];
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
        xs[k] = x; ys[k] = y; zs[k] = z;
        k++;
    }
    if (k === 0) return null;
    const lo = Math.floor((k - 1) * q), hi = Math.ceil((k - 1) * (1 - q));
    const sx = xs.subarray(0, k).sort(), sy = ys.subarray(0, k).sort(), sz = zs.subarray(0, k).sort();
    const box = new pc.BoundingBox();
    box.setMinMax(new pc.Vec3(sx[lo], sy[lo], sz[lo]), new pc.Vec3(sx[hi], sy[hi], sz[hi]));
    return box;
}

type SplatResourceLike = { centers?: ArrayLike<number> | null; centersVersion?: number; aabb?: pc.BoundingBox | null };

/** Local-space quantile boxes per splat resource (its centres don't change while it lives). */
const localBoxCache = new WeakMap<object, { version: number; box: pc.BoundingBox | null }>();

function splatLocalBounds(entity: pc.Entity): pc.BoundingBox | null {
    const gsplat = entity.gsplat as (pc.GSplatComponent & { resource?: SplatResourceLike | null; customAabb?: pc.BoundingBox | null }) | undefined;
    if (!gsplat) return null;
    const res = gsplat.resource;
    if (res?.centers && res.centers.length >= 3) {
        const version = res.centersVersion ?? 0;
        let cached = localBoxCache.get(res);
        if (!cached || cached.version !== version) {
            cached = { version, box: quantileBox(res.centers) };
            localBoxCache.set(res, cached);
        }
        if (cached.box) return cached.box;
    }
    return res?.aabb ?? gsplat.customAabb ?? null;
}

/** World bounds of everything that renders in `root`'s hierarchy (splats and meshes), or null. */
export function computeRenderBounds(root: pc.Entity): RenderBounds | null {
    let out: pc.BoundingBox | null = null;
    const add = (b: pc.BoundingBox) => {
        if (!out) out = new pc.BoundingBox(b.center.clone(), b.halfExtents.clone());
        else out.add(b);
    };
    root.forEach((node) => {
        const entity = node as pc.Entity;
        const local = splatLocalBounds(entity);
        if (local) {
            const world = new pc.BoundingBox();
            world.setFromTransformedAabb(local, entity.getWorldTransform());
            add(world);
        }
        for (const mi of entity.render?.meshInstances ?? []) add(mi.aabb);
        for (const mi of entity.model?.meshInstances ?? []) add(mi.aabb);
    });
    const box = out as pc.BoundingBox | null;
    if (!box) return null;
    const c = box.center, h = box.halfExtents;
    if (![c.x, c.y, c.z, h.x, h.y, h.z].every(Number.isFinite)) return null;
    return {
        center: [c.x, c.y, c.z],
        radius: h.length(),
        min: [c.x - h.x, c.y - h.y, c.z - h.z],
        max: [c.x + h.x, c.y + h.y, c.z + h.z],
    };
}
